import { useState, useRef, useCallback, useEffect } from 'react'
import { Dialog } from 'primereact/dialog'
import { Map as ImmutableMap } from 'immutable'
import useSession from '@aytm/app/swidget/base/session/useSession.ts'
import useTheme from '@aytm/app/swidget/base/modules/theme/useTheme.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import ExitSimulation from '@aytm/app/swidget/base/questions/_common/ExitSimulation.tsx'
import { AnswerID } from '@aytm/app/swidget/base/questions/types'
import { IEcommerceQuestionViewProps } from '../types.ts'
import style from './Kroger.module.scss'
import Header from './components/Header.tsx'
import Main from './components/Main.tsx'
import Product from './components/Product.tsx'
import Cart from './components/Cart.tsx'
import Footer from './components/Footer.tsx'
import CartRail from './components/CartRail.tsx'
import useLoadScriptInFrame from '../hooks/useLoadScriptInFrame.ts'

const KrogerView = ({ schema, response, checkout, setClosed, onUpdateResponse }: IEcommerceQuestionViewProps) => {
  const na = schema.answers.find(answer => answer.na)
  const totals = [
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v), 0),
    response.answers.reduce((prev, cur) => prev + (cur.id === na?.id ? 0 : cur.v * cur.price), 0)
  ]
  const indexById = new Map(response.answers.map((r, i) => [r.id, i]))
  const amountById = new Map(response.answers.map(r => [r.id, r.v]))
  const selectedAnswer = response.answers.find(r => r.selected)
  const selectedId = selectedAnswer?.id
  const findability = 'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)
  const pvRoot: HTMLElement = document.querySelector('.surveyWidgetFullsizeContainer')

  const [showCart, setShowCart] = useState<boolean>(false)
  const [needHint, setNeedHint] = useState(true)

  const containerRef = useRef<HTMLDivElement>(null)
  const cardRefsMapRef = useRef<ImmutableMap<string, HTMLDivElement>>(ImmutableMap())
  const frameIdRef = useRef<number | null>(null)
  const currentCenterElementRef = useRef<string | null>(null)
  const isScrollingRef = useRef<boolean>(false)

  const session = useSession()
  const timeline = useTimeline()
  const theme = useTheme()
  const viewport = useViewport()
  useLoadScriptInFrame('https://unpkg.com/@google/model-viewer', containerRef, viewport.mobile && viewport.emulated)

  const registerCardRef = useCallback((id: AnswerID, element: HTMLDivElement | null) => {
    if (element) {
      cardRefsMapRef.current = cardRefsMapRef.current.set(id, element)
    } else if (cardRefsMapRef.current.has(id)) {
      cardRefsMapRef.current = cardRefsMapRef.current.delete(id)
    }
  }, [])

  const findCenterElement = () => {
    if (isScrollingRef.current) return
    isScrollingRef.current = true
    if (frameIdRef.current !== null) {
      cancelAnimationFrame(frameIdRef.current)
    }
    frameIdRef.current = requestAnimationFrame(() => {
      if (!containerRef.current || 0 === cardRefsMapRef.current.size) {
        isScrollingRef.current = false
        frameIdRef.current = null
        return
      }

      const containerRect = containerRef.current.getBoundingClientRect()
      const containerCenter = containerRect.top + containerRect.height / 2

      let closestElement = null
      let minDistance = Infinity

      cardRefsMapRef.current.forEach((element, id) => {
        const cardRect = element.getBoundingClientRect()
        const cardCenter = cardRect.top + cardRect.height / 2
        const distance = Math.abs(containerCenter - cardCenter)

        if (distance < minDistance) {
          minDistance = distance
          closestElement = id
        }
      })

      if (closestElement && closestElement !== currentCenterElementRef.current) {
        currentCenterElementRef.current = closestElement
      }

      isScrollingRef.current = false
      frameIdRef.current = null
    })
  }

  const restorePosition = useCallback(() => {
    if (!currentCenterElementRef.current && containerRef.current) {
      containerRef.current.scrollTop = 0
    }
    if (cardRefsMapRef.current.has(currentCenterElementRef.current)) {
      const element = cardRefsMapRef.current.get(currentCenterElementRef.current)
      if (element) {
        requestAnimationFrame(() => {
          element.scrollIntoView({
            behavior: 'auto',
            block: 'center'
          })
        })
      }
    }
  }, [])

  useEffect(() => {
    if (!showCart && !selectedId) {
      restorePosition()
    }
  }, [showCart, selectedId, restorePosition])

  const scrollTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [])

  const clearSelection = () => {
    if (showCart) {
      setShowCart(false)
      timeline.log({ type: 'close_cart' })
    }
    if (selectedId) {
      timeline.log({ type: 'close_popup', ref: selectedId })
    }
    onUpdateResponse({
      answers: response.answers.map(r => ({ ...r, selected: false })),
      clicked: response.clicked
    })
  }

  const onItemSelect = (id: AnswerID) => {
    const index = indexById.get(id)
    if (index === undefined) return
    scrollTop()
    if (showCart) {
      setShowCart(false)
      timeline.log({ type: 'close_cart' })
    }
    response.answers[index].selected = true
    timeline.log({ target: '0,0', type: 'open_popup', ref: id })
    onUpdateResponse(response)
  }

  const onItemClose = () => {
    if (!selectedAnswer) return
    timeline.log({ type: 'close_popup', ref: selectedAnswer.id })
    selectedAnswer.selected = false
    onUpdateResponse(response)
  }

  const onCartOpen = () => {
    scrollTop()
    if (selectedAnswer) {
      timeline.log({ type: 'close_popup', ref: selectedAnswer.id })
      selectedAnswer.selected = false
      onUpdateResponse(response)
    }
    setShowCart(true)
    timeline.log({ type: 'open_cart' })
  }

  const onCartClose = () => {
    setShowCart(false)
    timeline.log({ type: 'close_cart' })
  }

  const onAddToCart = (id: AnswerID, qnt: number, inCarousel = false) => {
    const index = indexById.get(id)
    if (index === undefined) return
    response.answers[index].v = Math.max(0, response.answers[index].v + qnt)
    if (inCarousel || (!showCart && !selectedId)) {
      timeline.log({ target: '0,0', type: 'open_popup', ref: id })
      timeline.log({ type: 'add_to_cart', ref: id, target: '1' })
      timeline.log({ type: 'close_popup', ref: id })
    } else if (showCart) {
      timeline.log({ type: 'edit_in_cart', ref: id, target: response.answers[index].v.toString() })
    } else {
      timeline.log({ type: 'add_to_cart', ref: id, target: response.answers[index].v.toString() })
      if (findability) {
        timeline.log({ type: 'close_popup', ref: id })
      }
    }
    onUpdateResponse(response)
  }

  const onDeleteFromCart = (id: AnswerID) => {
    const index = indexById.get(id)
    if (index === undefined) return
    response.answers[index].v = 0
    timeline.log({ type: 'delete_from_cart', ref: id })
    onUpdateResponse(response)
  }

  const onHomeClick = () => {
    showCart ? onCartClose() : onItemClose()
  }

  useEffect(() => {
    if (na) {
      const naResponse = response.answers.find(answer => answer.id === na.id)
      if (naResponse && naResponse.v > 0) {
        checkout()
      }
    }
  }, [response, checkout, na])

  if (theme.style?.pv && !theme.style?.flutter && !pvRoot) return null

  return (
    <Dialog
      maskClassName={`${style.dialog} ${session.themeClassNames}`}
      appendTo={theme.style?.pv && pvRoot ? pvRoot : session.document?.body}
      onHide={() => {}}
      maximizable={true}
      maximized={true}
      visible={true}
    >
      <div
        className={`${style.container} ${viewport.mobile ? style.containerMobile : ''}`}
        onScroll={findCenterElement}
        ref={containerRef}
      >
        <Header
          schema={schema}
          total={totals[0]}
          findability={findability}
          clearSelection={clearSelection}
          onCartOpen={onCartOpen}
          onHomeClick={onHomeClick}
        />
        {selectedId ? (
          <Product
            answer={schema.answers.find(answer => answer.id === selectedId)}
            schema={schema}
            amountById={amountById}
            findability={findability}
            cartTotal={totals[0]}
            onItemClose={onItemClose}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            scrollTop={scrollTop}
          />
        ) : showCart ? (
          <Cart
            response={response}
            schema={schema}
            amountById={amountById}
            totals={totals}
            onCartClose={onCartClose}
            onAddToCart={onAddToCart}
            onDeleteFromCart={onDeleteFromCart}
            onItemSelect={onItemSelect}
            onUpdateResponse={onUpdateResponse}
            checkout={checkout}
            scrollTop={scrollTop}
          />
        ) : (
          <Main
            schema={schema}
            amountById={amountById}
            findability={findability}
            cartTotal={totals[0]}
            registerCardRef={registerCardRef}
            onItemSelect={onItemSelect}
            onAddToCart={onAddToCart}
            scrollTop={scrollTop}
          />
        )}
        <Footer
          findability={findability}
          showCart={showCart}
          showMain={!showCart && !selectedId}
          total={totals[0]}
          onHomeClick={onHomeClick}
          onCartOpen={onCartOpen}
          showHint={() => setNeedHint(true)}
          closeSimulation={setClosed}
        />
        {!findability && !showCart && !viewport.mobile && (
          <CartRail
            schema={schema}
            amountById={amountById}
            total={totals[1]}
            selectedId={selectedId}
            onItemSelect={onItemSelect}
            onItemClose={onItemClose}
            onGoToCart={onCartOpen}
            onQtyChange={onAddToCart}
          />
        )}
      </div>
      <ExitSimulation
        setClosed={setClosed}
        needExternalHint={needHint}
        setExternalHint={viewport.mobile ? setNeedHint : null}
        hintText={schema.instruction}
      />
    </Dialog>
  )
}

export default KrogerView
